import type { LucideIcon } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Sparkline } from "@/components/dashboard/Sparkline";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export interface KpiCardProps {
  /** Label visibile sopra il valore (i18n al call-site). */
  label: string;
  /** Valore primario (es. "152", "99.8%"). Se undefined → skeleton. */
  value: string | number | undefined;
  /** Riga di dettaglio sotto al valore (es. "348 act · 12 susp"). */
  hint?: string;
  /** Icona lucide per il chip top-right. */
  icon?: LucideIcon;
  /** Sparkline opzionale (max ~14 punti per buon rendering). */
  sparkline?: { values: readonly number[]; ariaLabel: string };
  /** Variante di tinta semantica (default neutral, warn per pending). */
  tone?: "default" | "warn" | "ok" | "danger";
  /** Errore caricamento → mostra dash; opzionale message. */
  error?: string;
  /** Loading state esplicito (override su value=undefined). */
  loading?: boolean;
}

const TONE_CLASSES: Record<NonNullable<KpiCardProps["tone"]>, string> = {
  default: "text-foreground",
  warn: "text-warning",
  ok: "text-success",
  danger: "text-destructive",
};

export function KpiCard({
  label,
  value,
  hint,
  icon: Icon,
  sparkline,
  tone = "default",
  error,
  loading,
}: KpiCardProps): JSX.Element {
  const isLoading = loading || (value === undefined && !error);

  return (
    <Card>
      <CardContent className="space-y-1.5 p-4">
        <div className="flex items-start justify-between gap-2">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </span>
          {Icon && <Icon className="size-4 text-muted-foreground" />}
        </div>

        {isLoading ? (
          <Skeleton className="h-8 w-16" />
        ) : error ? (
          <div
            className="text-2xl font-semibold tabular-nums text-muted-foreground"
            title={error}
          >
            —
          </div>
        ) : (
          <div className={cn("text-2xl font-semibold tabular-nums", TONE_CLASSES[tone])}>
            {value}
          </div>
        )}

        {hint && !isLoading && !error && (
          <div className="text-xs text-muted-foreground">{hint}</div>
        )}

        {sparkline && !isLoading && !error && (
          <Sparkline values={sparkline.values} ariaLabel={sparkline.ariaLabel} />
        )}
      </CardContent>
    </Card>
  );
}
